"use client";

import { useEffect } from "react";
import localFont from "next/font/local";
import "./globals.css";

import { reportClientIssue } from "@/components/system/ErrorCapture";
import { DEFAULT_MODE } from "@/lib/theme";
import { APP_UPDATED_MESSAGE, APP_UPDATED_TITLE, isStaleDeployError } from "@/lib/stale-deploy";

type BoundaryError = Error & { digest?: string };

const plusJakarta = localFont({
  // Self-hosted (28 Sep 2026): Google Fonts started returning a URL Turbopack's
  // next/font/google loader cannot parse ("queries have exactly one entry"),
  // failing every fresh prod build. Latin subset, variable weight 200-800.
  src: "./fonts/PlusJakartaSans-latin.woff2",
  variable: "--font-plus-jakarta",
  weight: "200 800",
  display: "swap",
});

const geistMono = localFont({
  // Self-hosted (1 Oct 2026, same break as Plus Jakarta on 28 Sep): Google Fonts intermittently returns
  // a URL Turbopack's next/font/google loader cannot parse ("queries have exactly one entry"),
  // failing fresh prod builds. Latin subset.
  src: "./fonts/GeistMono-latin.woff2",
  variable: "--font-geist-mono",
  weight: "100 900",
  display: "swap",
});

export function reportGlobalError(error: BoundaryError): void {
  void reportClientIssue({
    errorMessage: error.message || "Global render error",
    stack: error.stack ?? null,
    context: {
      boundary: "global",
      digest: error.digest ?? null,
    },
  });
}

export default function GlobalError({
  error,
  retry,
}: {
  error: BoundaryError;
  retry: () => void;
}) {
  useEffect(() => {
    reportGlobalError(error);
  }, [error]);
  // A deploy landed while this page was open: its Server Actions no longer
  // exist, so "Try again" re-renders the same stale page forever. Only a real
  // reload fetches the new version (same branch as app/cu/error.tsx).
  const stale = isStaleDeployError(error);

  return (
    <html
      lang="en"
      data-mode={DEFAULT_MODE}
      className={`${plusJakarta.variable} ${geistMono.variable} h-full antialiased`}
    >
      <body className="min-h-full bg-[hsl(var(--bg))] text-[hsl(var(--fg))]">
        <main
          style={{
            minHeight: "100vh",
            display: "grid",
            placeItems: "center",
            padding: "24px",
            background: "hsl(var(--bg))",
            color: "hsl(var(--fg))",
          }}
        >
          <section
            style={{
              width: "min(560px, 100%)",
              border: "1px solid hsl(var(--border))",
              borderRadius: "8px",
              background: "hsl(var(--surface))",
              padding: "24px",
              boxShadow: "0 20px 60px rgb(0 0 0 / 0.32)",
            }}
          >
            <div
              aria-hidden
              style={{
                height: "2px",
                background: "hsl(var(--chart-5))",
                marginBottom: "18px",
              }}
            />
            <p
              style={{
                margin: 0,
                color: "hsl(var(--chart-5))",
                fontSize: "10px",
                fontWeight: 700,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
              }}
            >
              Fowlboys console error
            </p>
            <h1
              style={{
                margin: "8px 0 0",
                color: "hsl(var(--fg-strong))",
                fontSize: "24px",
                lineHeight: 1.1,
              }}
            >
              {stale ? APP_UPDATED_TITLE : "Something broke"}
            </h1>
            <p
              style={{
                margin: "12px 0 0",
                color: "hsl(var(--fg-muted))",
                fontSize: "13px",
                lineHeight: 1.7,
              }}
            >
              {stale
                ? APP_UPDATED_MESSAGE
                : "It has been reported. Retry the console once the failure clears."}
            </p>
            <button
              type="button"
              onClick={stale ? () => window.location.reload() : retry}
              style={{
                marginTop: "20px",
                height: "36px",
                border: "1px solid hsl(var(--border))",
                borderRadius: "6px",
                background: "hsl(var(--surface-elev))",
                color: "hsl(var(--fg))",
                padding: "0 12px",
                fontSize: "12px",
                fontWeight: 700,
              }}
            >
              {stale ? "Reload" : "Try again"}
            </button>
          </section>
        </main>
      </body>
    </html>
  );
}
